import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Chip, FormControlLabel, Grid, IconButton, Paper, Stack,
  Switch, TextField, Typography, alpha, Button, MenuItem,
} from '@mui/material';
import { Add, Remove, LocalShipping } from '@mui/icons-material';
import { PORTER_SPECIAL_HANDLING } from '../../constants/porterMasters';
import { estimatePorterFare } from '../../services/businessService';
import { formatMoney } from '../../utils/currency';
import { resolveAssetUrl } from '../../utils/assetUrl';

export type PorterVehicleRow = {
  _id: string;
  name: string;
  image?: string;
  maxWeightKg?: number;
  helpersMax?: number;
  baseFare?: number;
  perKm?: number;
  capacityKg?: number;
  maxStops?: number;
};

export type PorterCategoryRow = {
  _id: string;
  name: string;
  icon?: string;
  image?: string;
  specialHandlingDefaults?: string[];
};

export type PorterPackageRow = {
  _id: string;
  name: string;
  weightKg?: number;
  image?: string;
  categoryId?: string | { _id?: string };
  specialHandling?: string[];
  insuranceEligible?: boolean;
};

export type PorterBookingState = {
  vehicleTypeId: string;
  categoryId: string;
  packageId: string;
  weightKg: string;
  helpersCount: string;
  specialHandling: string[];
  insuranceOpted: boolean;
  declaredValue: string;
  goodsDescription: string;
  receiverName: string;
  receiverPhone: string;
};

type Props = {
  accent: string;
  vehicles: PorterVehicleRow[];
  categories: PorterCategoryRow[];
  packages: PorterPackageRow[];
  value: PorterBookingState;
  onChange: (next: PorterBookingState) => void;
  pickup?: { lat: number; lng: number; address: string } | null;
  dropoff?: { lat: number; lng: number; address: string } | null;
  fieldErrors?: Record<string, string>;
};

function categoryIdOf(pkg: PorterPackageRow): string {
  if (!pkg.categoryId) return '';
  return typeof pkg.categoryId === 'object' ? String(pkg.categoryId._id || '') : String(pkg.categoryId);
}

export default function DispatchPorterOptions({
  accent, vehicles, categories, packages, value, onChange, pickup, dropoff, fieldErrors = {},
}: Props) {
  const [estimate, setEstimate] = useState<{ total?: number; helperCharge?: number; distanceKm?: number } | null>(null);
  const [estimating, setEstimating] = useState(false);

  const selectedVehicle = vehicles.find((v) => v._id === value.vehicleTypeId) || null;
  const maxHelpers = selectedVehicle?.helpersMax ?? 0;
  const helpersCount = Math.min(maxHelpers, Math.max(0, Number(value.helpersCount) || 0));

  const filteredPackages = useMemo(() => {
    if (!value.categoryId) return packages;
    return packages.filter((p) => {
      const cid = categoryIdOf(p);
      return !cid || cid === value.categoryId;
    });
  }, [packages, value.categoryId]);

  useEffect(() => {
    if (!vehicles.length) return;
    if (!value.vehicleTypeId || !vehicles.some((v) => v._id === value.vehicleTypeId)) {
      onChange({ ...value, vehicleTypeId: vehicles[0]._id, helpersCount: '0' });
    }
  }, [vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (helpersCount !== Number(value.helpersCount || 0)) {
      onChange({ ...value, helpersCount: String(helpersCount) });
    }
  }, [maxHelpers]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (partial: Partial<PorterBookingState>) => onChange({ ...value, ...partial });

  const applyCategory = (categoryId: string) => {
    const cat = categories.find((c) => c._id === categoryId);
    const nextHandling = cat?.specialHandlingDefaults?.length
      ? cat.specialHandlingDefaults
      : value.specialHandling;
    let packageId = value.packageId;
    if (packageId) {
      const pkg = packages.find((p) => p._id === packageId);
      const cid = pkg ? categoryIdOf(pkg) : '';
      if (cid && cid !== categoryId) packageId = '';
    }
    patch({ categoryId, packageId, specialHandling: nextHandling });
  };

  const applyPackage = (packageId: string) => {
    const pkg = packages.find((p) => p._id === packageId);
    if (!pkg) {
      patch({ packageId: '' });
      return;
    }
    const cid = categoryIdOf(pkg);
    patch({
      packageId,
      categoryId: cid || value.categoryId,
      weightKg: pkg.weightKg != null ? String(pkg.weightKg) : value.weightKg,
      specialHandling: pkg.specialHandling?.length ? pkg.specialHandling : value.specialHandling,
      insuranceOpted: pkg.insuranceEligible === false ? false : value.insuranceOpted,
    });
  };

  const runEstimate = async () => {
    if (!value.vehicleTypeId || !pickup || !dropoff) return;
    setEstimating(true);
    try {
      const data = await estimatePorterFare({
        vehicleTypeId: value.vehicleTypeId,
        helpersCount,
        weightKg: Number(value.weightKg) || 20,
        specialHandling: value.specialHandling,
        insuranceOpted: value.insuranceOpted,
        declaredValue: value.insuranceOpted ? Number(value.declaredValue) || 0 : 0,
        stops: [
          { type: 'pickup', lat: pickup.lat, lng: pickup.lng, address: pickup.address },
          { type: 'drop', lat: dropoff.lat, lng: dropoff.lng, address: dropoff.address },
        ],
      });
      const fare = (data?.fare || data) as { total?: number; helperCharge?: number };
      setEstimate({
        total: Number(fare?.total ?? data?.total ?? 0),
        helperCharge: Number(fare?.helperCharge ?? 0),
        distanceKm: Number(data?.distanceKm ?? 0),
      });
    } catch {
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  };

  return (
    <Stack spacing={1.25}>
      <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
        VEHICLE
      </Typography>
      {fieldErrors.vehicleTypeId && (
        <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.vehicleTypeId}</Typography>
      )}
      <Grid container spacing={1}>
        {vehicles.map((v) => {
          const selected = v._id === value.vehicleTypeId;
          const img = resolveAssetUrl(v.image || '');
          const canHelp = (v.helpersMax ?? 0) > 0;
          return (
            <Grid item xs={12} sm={6} key={v._id}>
              <Paper
                elevation={0}
                onClick={() => patch({ vehicleTypeId: v._id, helpersCount: canHelp ? value.helpersCount : '0' })}
                sx={{
                  p: 1.1,
                  cursor: 'pointer',
                  borderRadius: '12px',
                  border: `1.5px solid ${selected ? accent : '#e2e8f0'}`,
                  bgcolor: selected ? alpha(accent, 0.06) : '#fff',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Box sx={{
                  width: 52, height: 52, borderRadius: '10px', flexShrink: 0, bgcolor: '#e2e8f0',
                  backgroundImage: img ? `url(${img})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary',
                }}>
                  {!img ? <LocalShipping fontSize="small" /> : null}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={800} noWrap>{v.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {[
                      v.maxWeightKg != null ? `Max ${v.maxWeightKg} kg` : null,
                      canHelp ? `Helpers up to ${v.helpersMax}` : 'No helpers',
                      v.baseFare != null ? `From ${formatMoney(v.baseFare)}` : null,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Goods category"
            value={value.categoryId || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => applyCategory(e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Package template"
            value={value.packageId || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => applyPackage(e.target.value)}
          >
            <MenuItem value="">Custom weight</MenuItem>
            {filteredPackages.map((p) => (
              <MenuItem key={p._id} value={p._id}>
                {p.name}{p.weightKg != null ? ` · ${p.weightKg} kg` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField fullWidth size="small" type="number" label="Weight (kg)" value={value.weightKg}
            onChange={(e) => patch({ weightKg: e.target.value })} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{
            px: 1, py: 0.5, height: '100%', minHeight: 40,
            border: '1px solid #e2e8f0', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            opacity: maxHelpers > 0 ? 1 : 0.55,
          }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">Helpers</Typography>
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <IconButton size="small" disabled={helpersCount <= 0 || maxHelpers <= 0}
                onClick={() => patch({ helpersCount: String(helpersCount - 1) })}>
                <Remove fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={800} sx={{ minWidth: 18, textAlign: 'center' }}>
                {helpersCount}
              </Typography>
              <IconButton size="small" disabled={helpersCount >= maxHelpers || maxHelpers <= 0}
                onClick={() => patch({ helpersCount: String(helpersCount + 1) })}>
                <Add fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            {maxHelpers > 0
              ? `This vehicle supports up to ${maxHelpers} helper(s). Helper charge appears in estimate.`
              : 'Selected vehicle does not include helpers.'}
            {estimate?.helperCharge != null && helpersCount > 0
              ? ` Helper charge: ${formatMoney(estimate.helperCharge)}`
              : ''}
          </Typography>
        </Grid>
      </Grid>

      {filteredPackages.length > 0 && (
        <>
          <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
            PACKAGE CARDS
          </Typography>
          <Grid container spacing={1}>
            {filteredPackages.slice(0, 8).map((p) => {
              const selected = p._id === value.packageId;
              const img = resolveAssetUrl(p.image || '');
              return (
                <Grid item xs={6} sm={4} key={p._id}>
                  <Paper
                    elevation={0}
                    onClick={() => applyPackage(p._id)}
                    sx={{
                      p: 1, cursor: 'pointer', borderRadius: '10px',
                      border: `1.5px solid ${selected ? accent : '#e2e8f0'}`,
                      bgcolor: selected ? alpha(accent, 0.06) : '#fff',
                    }}
                  >
                    <Box sx={{
                      height: 48, borderRadius: '8px', mb: 0.75, bgcolor: '#e2e8f0',
                      backgroundImage: img ? `url(${img})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center',
                    }} />
                    <Typography variant="caption" fontWeight={800} display="block" noWrap>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.weightKg != null ? `${p.weightKg} kg` : 'Custom'}
                      {p.insuranceEligible ? ' · Insured' : ''}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Autocomplete
        multiple
        size="small"
        options={PORTER_SPECIAL_HANDLING.map((h) => h.value)}
        getOptionLabel={(v) => PORTER_SPECIAL_HANDLING.find((h) => h.value === v)?.label || v}
        value={value.specialHandling}
        onChange={(_, next) => patch({ specialHandling: next })}
        renderTags={(tags, getTagProps) => tags.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} size="small" label={option} />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Special handling"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          />
        )}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={value.insuranceOpted}
              onChange={(e) => patch({ insuranceOpted: e.target.checked })}
              color="primary"
            />
          }
          label="Insurance"
        />
        <TextField
          size="small"
          type="number"
          label="Declared value"
          disabled={!value.insuranceOpted}
          value={value.declaredValue}
          onChange={(e) => patch({ declaredValue: e.target.value })}
          sx={{ width: { sm: 160 } }}
        />
        <Button size="small" variant="outlined" onClick={runEstimate} disabled={estimating || !pickup || !dropoff}
          sx={{ borderColor: accent, color: accent }}>
          {estimating ? 'Estimating…' : 'Estimate fare'}
        </Button>
        {estimate?.total != null && (
          <Typography variant="body2" fontWeight={800}>
            {formatMoney(estimate.total)}
            {estimate.distanceKm != null ? ` · ${estimate.distanceKm.toFixed(1)} km` : ''}
          </Typography>
        )}
      </Stack>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Goods description" value={value.goodsDescription}
            onChange={(e) => patch({ goodsDescription: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Receiver name" value={value.receiverName}
            onChange={(e) => patch({ receiverName: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Receiver phone" value={value.receiverPhone}
            onChange={(e) => patch({ receiverPhone: e.target.value })} />
        </Grid>
      </Grid>
    </Stack>
  );
}
